import { useState, useEffect } from 'react';
import SectionHeader from '../../components/admin/SectionHeader';
import AdminModal from '../../components/admin/AdminModal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import FormField from '../../components/admin/FormField';
import Badge from '../../components/admin/Badge';
import DataTable from '../../components/admin/DataTable';

function AdminPlans({ apiFetch, token, toast }) {
  const [spView, setSpView] = useState('services');
  const [services, setServices] = useState([]);
  const [allPlans, setAllPlans] = useState([]);
  const [spPlanFilter, setSpPlanFilter] = useState('all');
  const [editingService, setEditingService] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [spSaving, setSpSaving] = useState(false);
  const [planAccessFields, setPlanAccessFields] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [priceLocks, setPriceLocks] = useState(null);
  const [priceLocksLoading, setPriceLocksLoading] = useState(false);

  const loadServicesAndPlans = async () => {
    try {
      const [sRes, pRes] = await Promise.all([
        apiFetch('/api/admin/services'),
        apiFetch('/api/admin/plans'),
      ]);
      const sData = await sRes.json();
      const pData = await pRes.json();
      setServices(Array.isArray(sData) ? sData : []);
      setAllPlans(Array.isArray(pData) ? pData : []);
    } catch { toast.error('Error', 'Failed to load services & plans'); }
  };

  useEffect(() => { loadServicesAndPlans(); }, []);

  const saveService = async (serviceData, isNew) => {
    setSpSaving(true);
    try {
      const method = isNew ? 'POST' : 'PUT';
      const url = isNew ? '/api/admin/services' : `/api/admin/services/${serviceData.key}`;
      const res = await apiFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(serviceData) });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
      toast.success('Saved', `Service ${isNew ? 'created' : 'updated'}`);
      setEditingService(null); await loadServicesAndPlans();
    } catch (err) { toast.error('Error', err.message); }
    finally { setSpSaving(false); }
  };

  const deleteService = async () => {
    if (!deleteConfirm) return;
    setSpSaving(true);
    try {
      const res = await apiFetch(`/api/admin/services/${deleteConfirm.id}`, { method: 'DELETE' });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
      toast.success('Deleted', 'Service removed');
      await loadServicesAndPlans();
    } catch (err) { toast.error('Error', err.message); }
    finally { setSpSaving(false); setDeleteConfirm(null); }
  };

  const savePlan = async (planData, isNew) => {
    setSpSaving(true);
    try {
      const method = isNew ? 'POST' : 'PUT';
      const url = isNew ? '/api/admin/plans' : `/api/admin/plans/${planData.slug}`;
      const res = await apiFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(planData) });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
      toast.success('Saved', `Plan ${isNew ? 'created' : 'updated'}`);
      setEditingPlan(null); await loadServicesAndPlans();
    } catch (err) { toast.error('Error', err.message); }
    finally { setSpSaving(false); }
  };

  const deletePlan = async () => {
    if (!deleteConfirm) return;
    setSpSaving(true);
    try {
      const res = await apiFetch(`/api/admin/plans/${deleteConfirm.id}`, { method: 'DELETE' });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
      toast.success('Deleted', 'Plan removed');
      await loadServicesAndPlans();
    } catch (err) { toast.error('Error', err.message); }
    finally { setSpSaving(false); setDeleteConfirm(null); }
  };

  const loadPlanAccessFields = async (slug) => {
    try {
      const res = await apiFetch(`/api/admin/plans/${slug}/access-fields`);
      const data = await res.json();
      setPlanAccessFields(Array.isArray(data) ? data : []);
    } catch { setPlanAccessFields([]); }
  };

  const savePlanAccessFields = async (slug, fields) => {
    try {
      await apiFetch(`/api/admin/plans/${slug}/access-fields`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ fields }) });
    } catch (err) { console.error('Save access fields error:', err); }
  };

  const serviceColumns = [
    { key: 'key', header: 'Key', render: row => <code>{row.key}</code> },
    { key: 'name', header: 'Name' },
    { key: 'description', header: 'Description', cellStyle: { color: 'var(--admin-text-secondary)' } },
    { key: 'status', header: 'Status', render: row => (
      <Badge variant={row.status === 'active' ? 'active' : row.status === 'presale' ? 'pending' : row.status === 'sold_out' ? 'suspended' : 'suspended'}>
        {row.status || 'active'}
      </Badge>
    )},
    { key: 'badge', header: 'Badge', render: row => row.badge
        ? <Badge variant="info">{row.badge}</Badge>
        : <span style={{ color: 'var(--admin-text-muted)' }}>—</span> },
    { key: 'sort_order', header: 'Sort', sortable: true },
    { key: 'active', header: 'Active', render: row => <Badge variant={row.active ? 'active' : 'suspended'}>{row.active ? 'Yes' : 'No'}</Badge> },
    { key: 'is_popular', header: 'Popular', render: row => row.is_popular ? <Badge variant="active"><i className="fa-solid fa-fire" style={{marginRight:4}}/>Popular</Badge> : <span style={{color:'var(--admin-text-muted)'}}>—</span> },
    { key: 'actions', header: 'Actions', render: row => (
      <div className="admin-btn-group">
        <button className="admin-btn admin-btn--sm admin-btn--secondary" onClick={() => setEditingService({ ...row })}>Edit</button>
        <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => setDeleteConfirm({ type: 'service', id: row.key, label: row.key })} disabled={spSaving}>Delete</button>
      </div>
    )},
  ];

  const filteredPlans = allPlans.filter(p => spPlanFilter === 'all' || p.service_key === spPlanFilter);

  const planColumns = [
    { key: 'slug', header: 'Slug', render: row => <code style={{ fontSize: '11px' }}>{row.slug}</code> },
    { key: 'service_key', header: 'Service', cellStyle: { color: 'var(--admin-text-secondary)' } },
    { key: 'name', header: 'Name' },
    { key: 'price', header: '$/mo', sortable: true, render: row => `$${row.price}` },
    { key: 'yearly_price', header: '$/yr', cellStyle: { color: 'var(--admin-text-secondary)' }, render: row => row.yearly_price ? `$${row.yearly_price}` : '—' },
    { key: 'trial_days', header: 'Trial', cellStyle: { color: 'var(--admin-text-secondary)' }, render: row => row.trial_days ? `${row.trial_days}d` : '—' },
    { key: 'active', header: 'Active', render: row => <Badge variant={row.active ? 'active' : 'suspended'}>{row.active ? 'Yes' : 'No'}</Badge> },
    { key: 'actions', header: 'Actions', render: row => (
      <div className="admin-btn-group">
        <button className="admin-btn admin-btn--sm admin-btn--secondary" onClick={() => {
          setEditingPlan({ ...row, features_text: (() => { try { return JSON.parse(row.features || '[]').join('\n'); } catch { return ''; } })() });
          loadPlanAccessFields(row.slug);
        }}>Edit</button>
        <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => setDeleteConfirm({ type: 'plan', id: row.slug, label: row.slug })} disabled={spSaving}>Delete</button>
      </div>
    )},
  ];


  return (
    <div>
      <SectionHeader title="Services & Plans" />

      <div className="admin-tabs" style={{ marginBottom: '16px' }}>
        {['services', 'plans', 'price-locks'].map(v => (
          <button key={v} className={`admin-tab ${spView === v ? 'admin-tab--active' : ''}`}
            onClick={() => {
              setSpView(v);
              setEditingService(null);
              setEditingPlan(null);
              if (v === 'price-locks' && priceLocks === null) {
                setPriceLocksLoading(true);
                apiFetch('/api/admin/price-locks')
                  .then(r => r.json())
                  .then(d => { setPriceLocks(d.locks || []); })
                  .catch(() => { setPriceLocks([]); })
                  .finally(() => setPriceLocksLoading(false));
              }
            }}>
            {v === 'services' ? 'Services' : v === 'plans' ? 'Plans' : 'Price Locks'}
          </button>
        ))}
      </div>

      {/* Services */}
      {spView === 'services' && (
        <div className="admin-card">
          <div className="admin-flex-between" style={{ marginBottom: '12px' }}>
            <h3 className="admin-card-title">Services</h3>
            <button className="admin-btn admin-btn--primary admin-btn--sm" onClick={() => setEditingService({ key: '', name: '', description: '', sort_order: 0, active: 1, badge: '', status: 'active', launch_date: '' })}>+ Add Service</button>
          </div>
          <DataTable
            columns={serviceColumns}
            data={services}
            emptyMessage="No services yet"
            emptyIcon="fa-solid fa-cubes"
          />

          {editingService && (
            <div className="admin-card" style={{ marginTop: '16px', background: 'var(--admin-elevated)' }}>
              <h4 style={{ margin: '0 0 14px 0', color: 'var(--admin-text-primary)', fontSize: '13px', fontWeight: '700' }}>{editingService.id ? 'Edit Service' : 'Add Service'}</h4>
              <div className="admin-grid-2">
                <FormField label="Key">
                  <input className="admin-input" type="text" value={editingService.key} onChange={e => setEditingService(prev => ({ ...prev, key: e.target.value }))} disabled={!!editingService.id} placeholder="e.g. n8n" />
                </FormField>
                <FormField label="Name">
                  <input className="admin-input" type="text" value={editingService.name} onChange={e => setEditingService(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. N8N" />
                </FormField>
                <div className="admin-grid-full">
                  <FormField label="Description">
                    <input className="admin-input" type="text" value={editingService.description || ''} onChange={e => setEditingService(prev => ({ ...prev, description: e.target.value }))} placeholder="Short description" />
                  </FormField>
                </div>
                <FormField label="Status">
                  <select className="admin-select" value={editingService.status || 'active'} onChange={e => setEditingService(prev => ({ ...prev, status: e.target.value }))}>
                    <option value="active">Active</option>
                    <option value="coming_soon">Coming Soon</option>
                    <option value="presale">Pre-sale</option>
                    <option value="sold_out">Sold Out</option>
                  </select>
                </FormField>
                <FormField label="Badge">
                  <select className="admin-select" value={editingService.badge || ''} onChange={e => setEditingService(prev => ({ ...prev, badge: e.target.value }))}>
                    <option value="">None</option>
                    <option value="new">New</option>
                    <option value="hot">Hot</option>
                    <option value="coming_soon">Coming Soon</option>
                    <option value="sold_out">Sold Out</option>
                    <option value="beta">Beta</option>
                    <option value="popular">Popular</option>
                  </select>
                </FormField>
                <FormField label="Launch Date" hint="Shown on card (e.g. Q3 2026)">
                  <input className="admin-input" type="text" value={editingService.launch_date || ''} onChange={e => setEditingService(prev => ({ ...prev, launch_date: e.target.value }))} placeholder="e.g. Q3 2026" />
                </FormField>
                <FormField label="Sort Order">
                  <input className="admin-input" type="number" value={editingService.sort_order || 0} onChange={e => setEditingService(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))} />
                </FormField>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '18px' }}>
                  <label className="admin-checkbox"><input type="checkbox" checked={editingService.active === 1 || editingService.active === true} onChange={e => setEditingService(prev => ({ ...prev, active: e.target.checked ? 1 : 0 }))} /> Active</label>
                </div>
              </div>
              <div className="admin-btn-group" style={{ marginTop: '14px' }}>
                <button className="admin-btn admin-btn--primary" onClick={() => saveService(editingService, !editingService.id)} disabled={spSaving || !editingService.key || !editingService.name}>{spSaving ? 'Saving...' : 'Save'}</button>
                <button className="admin-btn admin-btn--ghost" onClick={() => setEditingService(null)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Plans */}
      {spView === 'plans' && (
        <div className="admin-card">
          <div className="admin-flex-between" style={{ marginBottom: '12px' }}>
            <div className="admin-filter-pills" style={{ marginBottom: 0 }}>
              {['all', ...services.map(s => s.key)].map(k => (
                <button key={k} className={`admin-pill ${spPlanFilter === k ? 'admin-pill--active' : ''}`} onClick={() => setSpPlanFilter(k)}>{k}</button>
              ))}
            </div>
            <button className="admin-btn admin-btn--primary admin-btn--sm" onClick={() => {
              setPlanAccessFields([]); setEditingPlan({ slug: '', service_key: services[0]?.key || '', name: '', price: 0, yearly_price: '', currency: 'usd', price_id: '', yearly_price_id: '', product_id: '', trial_days: 0, commission_rate: null, renewal_price: null, renewal_price_yearly: null, supports_custom_domain: 0, is_popular: 0, tagline: '', features_text: '', sort_order: 0, active: 1 });
            }}>+ Add Plan</button>
          </div>
          <DataTable
            columns={planColumns}
            data={filteredPlans}
            emptyMessage="No plans found"
            emptyIcon="fa-solid fa-box-open"
          />

          {editingPlan && (
            <div className="admin-card" style={{ marginTop: '16px', background: 'var(--admin-elevated)' }}>
              <h4 style={{ margin: '0 0 14px 0', color: 'var(--admin-text-primary)', fontSize: '13px', fontWeight: '700' }}>{editingPlan.id ? 'Edit Plan' : 'Add Plan'}</h4>
              <div className="admin-grid-3">
                <FormField label="Slug">
                  <input className="admin-input" type="text" value={editingPlan.slug} onChange={e => setEditingPlan(prev => ({ ...prev, slug: e.target.value }))} disabled={!!editingPlan.id} placeholder="e.g. n8n-starter" />
                </FormField>
                <FormField label="Service">
                  <select className="admin-select" value={editingPlan.service_key} onChange={e => setEditingPlan(prev => ({ ...prev, service_key: e.target.value }))}>
                    {services.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
                  </select>
                </FormField>
                <FormField label="Name">
                  <input className="admin-input" type="text" value={editingPlan.name} onChange={e => setEditingPlan(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. N8N Starter" />
                </FormField>
                <FormField label="Tagline" hint="Shown below plan name on pricing page">
                  <input className="admin-input" type="text" value={editingPlan.tagline || ''} onChange={e => setEditingPlan(prev => ({ ...prev, tagline: e.target.value }))} placeholder="e.g. Ideal for individuals and small teams" />
                </FormField>
                <FormField label="Monthly Price ($)">
                  <input className="admin-input" type="number" step="0.01" value={editingPlan.price} onChange={e => setEditingPlan(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))} />
                </FormField>
                <FormField label="Yearly Price ($)" hint="Optional">
                  <input className="admin-input" type="number" step="0.01" value={editingPlan.yearly_price || ''} onChange={e => setEditingPlan(prev => ({ ...prev, yearly_price: e.target.value ? parseFloat(e.target.value) : null }))} placeholder="Optional" />
                </FormField>
                <FormField label="Renewal Price ($)" hint="Shown as 'Renews at $X' on pricing page. Leave blank to hide.">
                  <input className="admin-input" type="number" step="0.01" value={editingPlan.renewal_price || ''} onChange={e => setEditingPlan(prev => ({ ...prev, renewal_price: e.target.value ? parseFloat(e.target.value) : null }))} placeholder="Optional — e.g. full price after promo" />
                </FormField>
                <FormField label="Renewal Price Yearly ($)" hint="Shown as 'Renews at $X/yr'. Leave blank to use monthly renewal price.">
                  <input className="admin-input" type="number" step="0.01" value={editingPlan.renewal_price_yearly || ''} onChange={e => setEditingPlan(prev => ({ ...prev, renewal_price_yearly: e.target.value ? parseFloat(e.target.value) : null }))} placeholder="Optional" />
                </FormField>
                <FormField label="Trial Days">
                  <input className="admin-input" type="number" value={editingPlan.trial_days || 0} onChange={e => setEditingPlan(prev => ({ ...prev, trial_days: parseInt(e.target.value) || 0 }))} />
                </FormField>
                <FormField label="Commission Rate %" hint="Overrides system default">
                  <input className="admin-input" type="number" min="0" max="100" step="0.1" value={editingPlan.commission_rate || ''} onChange={e => setEditingPlan(prev => ({ ...prev, commission_rate: e.target.value ? parseFloat(e.target.value) : null }))} placeholder="System default" />
                </FormField>
                <FormField label="Monthly Price ID">
                  <input className="admin-input" type="text" value={editingPlan.price_id || ''} onChange={e => setEditingPlan(prev => ({ ...prev, price_id: e.target.value }))} placeholder="price_..." style={{ fontFamily: 'monospace' }} />
                </FormField>
                <FormField label="Yearly Price ID">
                  <input className="admin-input" type="text" value={editingPlan.yearly_price_id || ''} onChange={e => setEditingPlan(prev => ({ ...prev, yearly_price_id: e.target.value }))} placeholder="price_..." style={{ fontFamily: 'monospace' }} />
                </FormField>
                <FormField label="Product ID">
                  <input className="admin-input" type="text" value={editingPlan.product_id || ''} onChange={e => setEditingPlan(prev => ({ ...prev, product_id: e.target.value }))} placeholder="prod_..." style={{ fontFamily: 'monospace' }} />
                </FormField>
                <FormField label="Sort Order">
                  <input className="admin-input" type="number" value={editingPlan.sort_order || 0} onChange={e => setEditingPlan(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))} />
                </FormField>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', paddingTop: '18px' }}>
                  <label className="admin-checkbox"><input type="checkbox" checked={!!editingPlan.supports_custom_domain} onChange={e => setEditingPlan(prev => ({ ...prev, supports_custom_domain: e.target.checked ? 1 : 0 }))} /> Custom Domain</label>
                  <label className="admin-checkbox"><input type="checkbox" checked={editingPlan.active === 1 || editingPlan.active === true} onChange={e => setEditingPlan(prev => ({ ...prev, active: e.target.checked ? 1 : 0 }))} /> Active</label>
                  <label className="admin-checkbox" style={{ color: 'var(--admin-accent)', fontWeight: 600 }}><input type="checkbox" checked={!!editingPlan.is_popular} onChange={e => setEditingPlan(prev => ({ ...prev, is_popular: e.target.checked ? 1 : 0 }))} /> <i className="fa-solid fa-fire" style={{marginRight:3}}/> Most Popular</label>
                </div>
                <div className="admin-grid-full">
                  <FormField label="Features (one per line)">
                    <textarea className="admin-input" value={editingPlan.features_text || ''} onChange={e => setEditingPlan(prev => ({ ...prev, features_text: e.target.value }))} rows={5} placeholder={'Feature 1\nFeature 2\nFeature 3'} style={{ resize: 'vertical', fontFamily: 'monospace' }} />
                  </FormField>
                </div>
              </div>
              {/* Access Fields Editor */}
              <div style={{ marginTop: '14px', borderTop: '1px solid var(--admin-border)', paddingTop: '14px' }}>
                <div className="admin-flex-between" style={{ marginBottom: '8px' }}>
                  <label className="admin-label" style={{ marginBottom: 0 }}>ACCESS FIELDS</label>
                  <button className="admin-btn admin-btn--sm admin-btn--secondary" onClick={() => setPlanAccessFields(prev => [...prev, { field_key: '', field_label: '', field_type: 'url', sort_order: prev.length }])}>+ Add Field</button>
                </div>
                {planAccessFields.map((af, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
                    <input className="admin-input" type="text" value={af.field_key} placeholder="field_key" onChange={e => { const arr = [...planAccessFields]; arr[idx] = { ...arr[idx], field_key: e.target.value }; setPlanAccessFields(arr); }} style={{ fontSize: '11px' }} />
                    <input className="admin-input" type="text" value={af.field_label} placeholder="Field Label" onChange={e => { const arr = [...planAccessFields]; arr[idx] = { ...arr[idx], field_label: e.target.value }; setPlanAccessFields(arr); }} style={{ fontSize: '11px' }} />
                    <select className="admin-select" value={af.field_type || 'url'} onChange={e => { const arr = [...planAccessFields]; arr[idx] = { ...arr[idx], field_type: e.target.value }; setPlanAccessFields(arr); }} style={{ fontSize: '11px' }}>
                      <option value="url">URL</option><option value="text">Text</option><option value="textarea">Free Text</option>
                    </select>
                    <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => setPlanAccessFields(prev => prev.filter((_, i) => i !== idx))}>x</button>
                  </div>
                ))}
              </div>
              <div className="admin-btn-group" style={{ marginTop: '14px' }}>
                <button className="admin-btn admin-btn--primary" onClick={() => {
                  const d = { ...editingPlan, features: JSON.stringify((editingPlan.features_text || '').split('\n').filter(f => f.trim())), commission_rate: editingPlan.commission_rate || null, is_popular: editingPlan.is_popular ? 1 : 0, tagline: editingPlan.tagline || null };
                  delete d.features_text;
                  savePlan(d, !editingPlan.id).then(() => { if (d.slug) savePlanAccessFields(d.slug, planAccessFields); });
                }} disabled={spSaving || !editingPlan.slug || !editingPlan.name}>{spSaving ? 'Saving...' : 'Save'}</button>
                <button className="admin-btn admin-btn--ghost" onClick={() => setEditingPlan(null)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Price Locks */}
      {spView === 'price-locks' && (
        <div className="admin-card">
          <div className="admin-flex-between" style={{ marginBottom: '12px' }}>
            <h3 className="admin-card-title">Subscription Price Locks</h3>
            <button className="admin-btn admin-btn--sm admin-btn--secondary" onClick={() => {
              setPriceLocksLoading(true);
              apiFetch('/api/admin/price-locks')
                .then(r => r.json())
                .then(d => { setPriceLocks(d.locks || []); })
                .catch(() => { setPriceLocks([]); })
                .finally(() => setPriceLocksLoading(false));
            }}>Refresh</button>
          </div>
          {priceLocksLoading ? (
            <p style={{ color: 'var(--admin-text-secondary)', fontSize: '13px' }}>Loading...</p>
          ) : !priceLocks || priceLocks.length === 0 ? (
            <p style={{ color: 'var(--admin-text-secondary)', fontSize: '13px' }}>No price locks recorded yet.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table" style={{ width: '100%', fontSize: '12px' }}>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Plan</th>
                    <th>Interval</th>
                    <th>Locked Price</th>
                    <th>Expires</th>
                    <th>Days Remaining</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {priceLocks.map(lock => (
                    <tr key={lock.id}>
                      <td style={{ fontFamily: 'monospace', fontSize: '11px' }}>{lock.customer_email}</td>
                      <td><code style={{ fontSize: '11px' }}>{lock.plan_slug}</code></td>
                      <td>{lock.interval}</td>
                      <td>${Number(lock.locked_price).toFixed(2)}</td>
                      <td style={{ fontSize: '11px' }}>{new Date(lock.lock_expires_at).toLocaleDateString()}</td>
                      <td style={{ textAlign: 'center' }}>{lock.days_remaining}</td>
                      <td>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontSize: '10px',
                          fontWeight: 700,
                          background: lock.expired ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)',
                          color: lock.expired ? '#f87171' : '#34d399',
                        }}>
                          {lock.expired ? 'Expired' : 'Active'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirm */}
      <ConfirmDialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={deleteConfirm?.type === 'service' ? deleteService : deletePlan}
        title={`Delete ${deleteConfirm?.type === 'service' ? 'Service' : 'Plan'}`}
        message={`Are you sure you want to delete "${deleteConfirm?.label}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
}

export default AdminPlans;
